import { Link } from "@tanstack/react-router";
import { EmptyState, ErrorState, Skeleton } from "@solarkey/ui";

/**
 * Router-level fallbacks.
 *
 * These are wired as `defaultErrorComponent` / `defaultNotFoundComponent` /
 * `defaultPendingComponent` on the router, so EVERY route gets them without each
 * screen opting in.
 *
 * Why they matter (QA, 25 Oct):
 *  - With no `defaultErrorComponent`, TanStack installs no CatchBoundary on any
 *    route, so one screen throwing unmounted the whole tree — the rail and top bar
 *    went with it and the user was left on a white page with no way back but typing
 *    a URL. An error must stay contained to the content area.
 *  - With no `defaultNotFoundComponent`, an unknown URL rendered the library's
 *    unstyled `<p>Not Found</p>` inside the shell.
 *  - With no `defaultPendingComponent`, the route transition fallback was `null`,
 *    so navigation flashed an empty content area.
 */

/** Where "start again" goes. The root route is the composition-aware home. */
const HOME = "/";

export function RouteErrorComponent({ error, reset }: { error?: unknown; reset?: () => void }) {
  // The message is for a person, so prefer the thrown message and keep the stack
  // out of the glass. Nothing here assumes the error is an Error instance.
  const message =
    (error as { message?: string } | undefined)?.message ??
    "Something went wrong while drawing this screen.";
  return (
    <div className="sk-route-fallback" role="alert">
      <ErrorState
        title="This screen stopped responding"
        message={message}
        onRetry={reset ?? (() => window.location.reload())}
      />
      <p className="sk-route-fallback__nav">
        <Link to={HOME}>Back to Home</Link>
      </p>
    </div>
  );
}

export function RouteNotFoundComponent() {
  return (
    <div className="sk-route-fallback">
      <EmptyState
        variant="no_data"
        title="That page does not exist"
        message="The address may be mistyped, or the screen may have moved. Nothing is wrong with your data."
      />
      <p className="sk-route-fallback__nav">
        <Link to={HOME}>Back to Home</Link>
      </p>
    </div>
  );
}

export function RoutePendingComponent() {
  // A skeleton, never a spinner (UX-003): the shell stays, the content area shows
  // the shape of what is coming.
  return (
    <div className="sk-route-fallback" aria-busy="true" aria-live="polite">
      <Skeleton />
    </div>
  );
}
